import { useMemo, useRef } from 'react';
import css from './SizeBar.module.css';

/**
 * Helper function to determine if a shoe size filter is selected
 * @param {string} activeSize - The active size filter string (e.g., "shoe-size-us-11,shoe-size-fr-42,...")
 * @param {string} size - The size option value (e.g., "11+", "5-6")
 * @returns {boolean}
 */
function isShoeSizeSelected(activeSize, size) {
  if (!activeSize) {
    return false;
  }

  // Extract the numeric size from the first size tag in activeSize
  // e.g., "shoe-size-us-11,shoe-size-fr-42,..." => "11"
  const firstSizeTag = activeSize.split(',')[0];
  const extractedSize = firstSizeTag.split('-').pop();

  // Special case: size 11 matches "11+" filter
  if (extractedSize === '11' && size === '11+') {
    return true;
  }

  // Direct match with the size option
  return extractedSize === size;
}

const SizeBar = ({ sizeOptions, activeCategory, activeSize, onSizeChange }) => {
  const scrollRef = useRef(null);

  const handleSizeClick = useMemo(() => {
    return size => {
      return () => onSizeChange(size);
    };
  }, [onSizeChange]);

  const isSizeSelected = useMemo(() => {
    return size => {
      // Handle "ALL SIZES" case (empty size)
      if (!activeSize && size === '') {
        return true;
      }

      // Handle clothing category
      if (activeCategory === 'clothing') {
        return activeSize === size;
      }

      // Handle shoe category
      return isShoeSizeSelected(activeSize, size);
    };
  }, [activeCategory, activeSize]);

  return (
    <div className={css.wrapper}>
      <div className={css.container} ref={scrollRef}>
        {sizeOptions.map(size => (
          <button
            key={size.value}
            className={`${css.sizeButton} ${isSizeSelected(size.value) ? css.selected : ''}`}
            onClick={handleSizeClick(size.value)}
          >
            {size.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SizeBar;
